import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Radio,
	RadioGroup,
	Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
	open: boolean;
	onClose: () => void;
	// True when this intervention promoted the member from "Pending intake" to
	// "Intake completed", so deleting it can revert the member status.
	canRevertStatus: boolean;
	// The intervention nature that caused the promotion, named in the message so
	// the staff can see why the status changed.
	nature?: string;
	onDelete: (revertStatus: boolean) => void;
}

const DeleteInterventionDialog = ({
	open,
	onClose,
	canRevertStatus,
	nature,
	onDelete,
}: Props) => {
	const { t } = useTranslation();
	// Reverting undoes the status change this intervention caused, so it is the
	// default choice.
	const [revertStatus, setRevertStatus] = useState(true);

	// The dialog stays mounted between openings, so reset the choice each time.
	useEffect(() => {
		if (open) setRevertStatus(true);
	}, [open]);

	return (
		<Dialog fullWidth open={open} onClose={onClose}>
			<DialogTitle variant='h6'>
				{t('preAdmission.deleteConfirmation')}
			</DialogTitle>
			<DialogContent sx={{ paddingTop: 0 }}>
				<Typography variant='body1'>{t('case.interventionTips')}</Typography>
				{canRevertStatus && (
					<Box
						sx={{
							display: 'flex',
							gap: 1.5,
							marginTop: 2,
							padding: 2,
							borderRadius: 1,
							backgroundColor: 'chipPrimary.main',
						}}
					>
						<InfoOutlinedIcon
							color='primary'
							fontSize='small'
							sx={{ marginTop: '2px' }}
						/>
						<Box>
							{/* The reason and the question read as one sentence, so they
							share a single paragraph rather than competing for emphasis. */}
							<Typography variant='body2'>
								{nature
									? t('case.interventionRevertStatusTips', { nature })
									: t('case.interventionRevertStatusTipsWithoutNature')}{' '}
								{t('case.interventionRevertStatusQuestion')}
							</Typography>
							<RadioGroup
								value={revertStatus ? 'revert' : 'keep'}
								onChange={(event) =>
									setRevertStatus(event.target.value === 'revert')
								}
								sx={{ marginTop: 1 }}
							>
								<FormControlLabel
									value='revert'
									control={<Radio size='small' />}
									label={
										<Typography variant='body2'>
											{t('case.revertMemberStatusOption')}
										</Typography>
									}
								/>
								<FormControlLabel
									value='keep'
									control={<Radio size='small' />}
									label={
										<Typography variant='body2'>
											{t('case.keepMemberStatusOption')}
										</Typography>
									}
								/>
							</RadioGroup>
						</Box>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>{t('button.cancel')}</Button>
				<Button
					variant='contained'
					onClick={() => {
						onDelete(canRevertStatus && revertStatus);
						onClose();
					}}
				>
					{t('button.delete')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DeleteInterventionDialog;
